import { ThemeService } from './../../../core/services/theme.service';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgxMaterialTimepickerTheme } from 'ngx-material-timepicker';
import { EMPTY, Subscription, switchMap, takeUntil } from 'rxjs';
import { GroupService } from 'src/app/core/services/group.service';
import { ScheduleService } from '../../../core/services/schedule.service';
import { BaseComponent } from './../../../core/components/base/base.component';
import { AppointmentTypeService } from './../../../core/services/appointment-type.service';
import { LocationService } from './../../../core/services/location.service';
import { typeLocationNames } from './../../../core/utils/practice-locations';
import { FeedbackService } from './../../../core/services/feedback.service';
import { SurveyTemplateService } from '../../../core/services/survey-template.service';
import { AppointmentTypeSurveyTemplateService } from '../../../core/services/appointment-type-survey-template.service';
import * as moment from 'moment';
import { DEFAULT_EMAILS, SIMPLIFIED_EMAILS } from './../../../core/data/app.data';
import { APPOINTMENT_DURATION_OPTIONS } from './../../../core/utils/appointment-duration';
import { effectivePatientsLimit, effectivePatientsPerTimeSlot } from './../../../core/utils/slot-capacity';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';

@Component({
  selector: 'app-schedule-settings',
  templateUrl: './schedule-settings.component.html',
  styleUrls: ['./schedule-settings.component.scss']
})
export class ScheduleSettingsComponent extends BaseComponent implements OnInit {

  days = [
    { name: 'Monday', selected: false },
    { name: 'Tuesday', selected: false },
    { name: 'Wednesday', selected: false },
    { name: 'Thursday', selected: false },
    { name: 'Friday', selected: false },
    { name: 'Saturday', selected: false },
    { name: 'Sunday', selected: false }
  ];


  darkTheme!: NgxMaterialTimepickerTheme;

  appointmentTypes: any[] = [];
  editingAppointmentType?: any = {};
  /** The group's practice locations; an appointment type can be limited to some of them. */
  locations: any[] = [];
  editingLocation: any = {};
  currentPickerIndex = -1;
  currentPickerField: string = '';
  //currentHighlightTarget: string = '';

  timeSettings: any = {
    timeSlotDuration: '30',
    startTime: '08:00',
    endTime: '17:00'
  }
  /** One list for the schedule's Duration select AND the per-type override, so they cannot drift. */
  readonly durationOptions = APPOINTMENT_DURATION_OPTIONS;
  endTimeInvalid: boolean = false;

  /** Booking capacity — when a slot counts as full and booking into it shows a conflict warning. */
  capacitySettings: { patientsLimit: 'capped' | 'unlimited', patientsPerTimeSlot: number } = {
    patientsLimit: 'capped',
    patientsPerTimeSlot: 1,
  };

  scheduleId: any = null;
  groupId: any = null;
  group?: any;
  groupUsers: any[] = [];

  /** Synced from `selectedGroup$` when the group stream emits. */
  calendarSplitByPhysician = false;

  // Survey template linking properties
  surveyTemplates: any[] = [];
  linkedSurveyTemplates: any[] = [];
  selectedSurveyTemplateId: string | null = null;
  isLinking: boolean = false;

  @ViewChild('availabilitySettingsDialog') availabilitySettingsDialog?: TemplateRef<any>;
  @ViewChild('timeslotSettingsDialog') timeslotSettingsDialog?: TemplateRef<any>;
  @ViewChild('capacitySettingsDialog') capacitySettingsDialog?: TemplateRef<any>;
  @ViewChild('appointmentTypeDialog') appointmentTypeDialog?: TemplateRef<any>;
  @ViewChild('locationDialog') locationDialog?: TemplateRef<any>;
  @ViewChild('appointmentReminderDialog') appointmentReminderDialog?: TemplateRef<any>;


  /** A state unit (tasks/state-mode.md): nothing is sent to patients, so there are no messages or reminders to set. */
  get stateMode(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    private scheduleService: ScheduleService,
    private appointmentTypeService: AppointmentTypeService,
    private locationService: LocationService,
    private groupService: GroupService,
    private themeService: ThemeService,
    fs: FeedbackService,
    md: MatDialog,
    private surveyTemplateService: SurveyTemplateService,
    private appointmentTypeSurveyTemplateService: AppointmentTypeSurveyTemplateService
  ) {
    super(fs, md);
    this.scheduleService.schedule$.subscribe((schedule: any) => {
      if (schedule) {
        this.group = this.groupService.group;
        const { id, groupId, availableDays, timeSlotDuration, startTime, endTime } = schedule;
        this.scheduleId = id;
        this.groupId = groupId;
        this.days.forEach(day => {
          day.selected = availableDays.includes(day.name);
        })
        this.timeSettings = { timeSlotDuration, startTime, endTime };
        this.capacitySettings = {
          patientsLimit: effectivePatientsLimit(schedule),
          patientsPerTimeSlot: effectivePatientsPerTimeSlot(schedule),
        };
      }
    });

    this.locationService.locations$
      .pipe(takeUntil(this.destroy$))
      .subscribe(locations => {
        this.locations = locations || [];
      });

    this.appointmentTypeService.appointmentTypes$.subscribe(appointmentTypes => {
      if (appointmentTypes) {
        this.appointmentTypes = appointmentTypes;
      }
    });

    this.themeService.isDarkMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDarkMode) => {
        this.darkTheme = this.themeService.getTimepickerTheme(isDarkMode);
      });

  }

  ngOnInit(): void {
    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe((g) => {
        if (g) {
          this.group = g;
        }
        this.calendarSplitByPhysician = !!g?.calendarSplitByPhysician;
      });
    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: any[]) => {
        this.groupUsers = (users || []).filter(
          (u: any) => u.userType === 'doctor' && u.userId !== '511f39e1-ff14-4a6d-b35a-6fcc22923b50',
        );
      });
  }

  openEditAvailabilityDialog(event?: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();

    const dialogRef = this.dialog.open(this.availabilitySettingsDialog!);

    dialogRef.afterClosed().subscribe((updatedDays: any[]) => {
      if (updatedDays) {
        this.scheduleService.updateSchedule({
          id: this.scheduleId,
          availableDays: updatedDays.filter(day => day.selected).map(day => day.name),
        })
          .pipe(takeUntil(this.destroy$)).subscribe(
            (response: any) => {
              if (response) {
                this.showToastMessage('Schedule availability updated successfully');
                this.groupService.setSelectedGroup(this.groupService.group);
              }
            },
            (error) => {
              console.error('Error updating schedule availability:', error);
              this.showToastMessage('There was an error updating your schedule availability. Please try again.');
            });
      }
    });
  }

  openEditTimeslotDialog(event?: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();

    const dialogRef = this.dialog.open(this.timeslotSettingsDialog!);

    dialogRef.afterClosed().subscribe((timeSettings: any) => {
      if (timeSettings) {
        this.scheduleService.updateSchedule({
          id: this.scheduleId,
          timeSlotDuration: timeSettings.timeSlotDuration,
          startTime: timeSettings.startTime,
          endTime: timeSettings.endTime,
        })
          .pipe(takeUntil(this.destroy$)).subscribe(
            (response: any) => {
              if (response) {
                this.showToastMessage('Schedule time settings updated successfully');
                this.groupService.setSelectedGroup(this.groupService.group);
              }
            },
            (error) => {
              console.error('Error updating schedule time settings:', error);
              this.showToastMessage('There was an error updating your schedule time settings. Please try again.');
            });
      }
    });
  }

  openEditCapacityDialog(event?: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();

    // Edit a copy so Cancel / clicking away leaves the panel showing what is saved.
    const draft = { ...this.capacitySettings };
    const dialogRef = this.dialog.open(this.capacitySettingsDialog!, { data: draft });

    dialogRef.afterClosed().subscribe((capacity: any) => {
      if (!capacity) return;
      const perSlot = Math.floor(Number(capacity.patientsPerTimeSlot));
      if (capacity.patientsLimit === 'capped' && !(perSlot >= 1 && perSlot <= 100)) {
        this.showToastMessage('Patients per time slot must be a whole number from 1 to 100.');
        return;
      }
      this.scheduleService.updateSchedule({
        id: this.scheduleId,
        patientsLimit: capacity.patientsLimit,
        ...(capacity.patientsLimit === 'capped' ? { patientsPerTimeSlot: perSlot } : {}),
      })
        .pipe(takeUntil(this.destroy$)).subscribe(
          (response: any) => {
            if (response) {
              this.showToastMessage('Booking capacity updated successfully');
              this.groupService.setSelectedGroup(this.groupService.group);
            }
          },
          (error) => {
            console.error('Error updating booking capacity:', error);
            this.showToastMessage('There was an error updating your booking capacity. Please try again.');
          });
    });
  }

  capacityHint(): string {
    const { patientsLimit, patientsPerTimeSlot } = this.capacitySettings;
    if (patientsLimit === 'unlimited') {
      return 'Overlapping bookings are allowed without a warning.';
    }
    return patientsPerTimeSlot === 1
      ? 'Booking into a slot that already has an appointment or blocked event shows a scheduling conflict warning.'
      : `Booking into a slot that already has ${patientsPerTimeSlot} appointments or blocked events shows a scheduling conflict warning.`;
  }

  validateEndSlot(timeSettings: any) {
    const startTime = timeSettings.startTime;
    const endTime = timeSettings.endTime;

    if (startTime && endTime) {
      const startMoment = moment(startTime, 'HH:mm');
      const endMoment = moment(endTime, 'HH:mm');

      if (endMoment.isSameOrBefore(startMoment)) {
        this.endTimeInvalid = true;
      } else {
        this.endTimeInvalid = false;
      }
    }
  }

  openAppointmentTypeDialog(event?: MouseEvent | null, appointmentType?: any) {
    event?.preventDefault();
    event?.stopPropagation();

    if (appointmentType) {
      this.editingAppointmentType = appointmentType;
    } else {
      let newBgColor = this.getRandomColor();
      while (this.colorExists(newBgColor)) {
        newBgColor = this.getRandomColor(); // Keep generating until unique
      }
      const newTextColor = this.isContrastEnough(newBgColor, '#FFFFFF') ? '#FFFFFF' : '#000000';
      this.editingAppointmentType = {
        id: this.appointmentTypes.length + 1,
        groupId: this.groupId,
        type: '',
        backgroundColor: newBgColor,
        textColor: newTextColor,
        canDelete: true,
        isProcedure: false,
        suppressSending: false,
        sendReminders: true,
        reminderMessage: '',
        //starveTime: '',
        //arriveTime: '',
        arriveLocation: '',
        defaultPhysicianId: null,
        // null = "use the schedule's duration" — tasks/appointment-type-duration.md
        durationMinutes: null,
        // null = offered at every location
        locationIds: null,
      }
    }
    let dialogRef = this.dialog.open(this.appointmentTypeDialog!, { width: '600px' });

    // Load survey templates and linked survey templates when dialog opens
    this.loadSurveyTemplates();
    if (this.editingAppointmentType?.id) {
      this.loadLinkedSurveyTemplates();
    }

    dialogRef.afterClosed().subscribe(updatedAppointmentType => {
      if (updatedAppointmentType) {
        updatedAppointmentType = {
          ...updatedAppointmentType,
          confirmationMessage: updatedAppointmentType?.confirmationMessage ? updatedAppointmentType.confirmationMessage : (updatedAppointmentType?.isProcedure ? DEFAULT_EMAILS.THEATRE_CONFIRMATION : DEFAULT_EMAILS.CONFIRMATION),
          rescheduleMessage: updatedAppointmentType?.rescheduleMessage ? updatedAppointmentType.rescheduleMessage : (updatedAppointmentType?.isProcedure ? DEFAULT_EMAILS.THEATRE_RESCHEDULE : DEFAULT_EMAILS.RESCHEDULE),
          cancellationMessage: updatedAppointmentType?.cancellationMessage ? updatedAppointmentType.cancellationMessage : (updatedAppointmentType?.isProcedure ? DEFAULT_EMAILS.THEATRE_CANCELLATION : DEFAULT_EMAILS.CANCELLATION),
          autoReminderMessage: updatedAppointmentType?.autoReminderMessage ? updatedAppointmentType?.autoReminderMessage : (updatedAppointmentType?.isProcedure ? DEFAULT_EMAILS.THEATRE_REMINDER : DEFAULT_EMAILS.REMINDER),
          trunConfirmationMessage: updatedAppointmentType?.trunConfirmationMessage ? updatedAppointmentType.trunConfirmationMessage : (updatedAppointmentType?.isProcedure ? SIMPLIFIED_EMAILS.THEATRE_CONFIRMATION : SIMPLIFIED_EMAILS.CONFIRMATION),
          trunRescheduleMessage: updatedAppointmentType?.trunRescheduleMessage ? updatedAppointmentType.trunRescheduleMessage : (updatedAppointmentType?.isProcedure ? SIMPLIFIED_EMAILS.THEATRE_RESCHEDULE : SIMPLIFIED_EMAILS.RESCHEDULE),
          trunCancellationMessage: updatedAppointmentType?.trunCancellationMessage ? updatedAppointmentType.trunCancellationMessage : (updatedAppointmentType?.isProcedure ? SIMPLIFIED_EMAILS.THEATRE_CANCELLATION : SIMPLIFIED_EMAILS.CANCELLATION),
          trunReminderMessage: updatedAppointmentType?.trunReminderMessage ? updatedAppointmentType?.trunReminderMessage : (updatedAppointmentType?.isProcedure ? SIMPLIFIED_EMAILS.THEATRE_REMINDER : SIMPLIFIED_EMAILS.REMINDER),
        }
        const index = this.appointmentTypes.findIndex(at => at.id === updatedAppointmentType.id);
        if (index !== -1) {
          this.appointmentTypes[index] = updatedAppointmentType;
        } else {
          delete updatedAppointmentType.id;
          this.appointmentTypes.push(updatedAppointmentType);
        }

        this.saveAppointmentTypes();
      }
    })
  }

  /** Adds or edits a location in a dialog, then saves the whole list. The first location is the default. */
  openLocationDialog(event?: MouseEvent | null, location?: any) {
    event?.preventDefault();
    event?.stopPropagation();

    this.editingLocation = location ? { ...location } : { name: '', isDefault: this.locations.length === 0 };
    this.dialog.open(this.locationDialog!, { width: '400px' }).afterClosed().subscribe(result => {
      if (!result) return;
      let list = location ? this.locations.map(l => (l.id === location.id ? result : l)) : [...this.locations, result];
      if (result.isDefault) {
        list = list.map(l => (l === result ? l : { ...l, isDefault: false }));
      }
      this.saveLocations(list);
    });
  }

  /** Removes a location. The default stays until another location is made the default. */
  async deleteLocation(location: any) {
    if (location.isDefault && this.locations.length > 1) {
      this.showToastMessage('Make another location the default before removing this one.');
      return;
    }
    if (await this.showConfirmationDialog()) {
      this.saveLocations(this.locations.filter(l => l.id !== location.id));
    }
  }

  private saveLocations(locations: any[]) {
    this.locationService.updateLocations(locations)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.showToastMessage('Locations updated successfully'),
        error: (error) => this.showToastMessage(error?.error?.message || 'There was an error updating the locations. Please try again.'),
      });
  }

  /** The locations a type is limited to, for its chip; empty when it is offered everywhere. */
  typeLocationsLabel(appointmentType: any): string {
    return typeLocationNames(appointmentType, this.locations).join(', ');
  }

  getAvailableDaysList(days: any[]) {
    return days.filter(d => d.selected).map(d => d.name).join(', ');
  }


  private getRandomColor(): string {
    let color = Math.floor(Math.random() * 16777215).toString(16);
    // Ensure the color is 6 characters long
    while (color.length < 6) {
      color = '0' + color;
    }
    return '#' + color;
  }

  private isContrastEnough(bgColor: string, textColor: string): boolean {
    const color = (c: string) => {
      const hex = c.substring(1); // strip #
      const rgb = parseInt(hex, 16); // convert rrggbb to decimal
      const r = (rgb >> 16) & 0xff; // extract red
      const g = (rgb >> 8) & 0xff; // extract green
      const b = (rgb >> 0) & 0xff; // extract blue

      // https://stackoverflow.com/a/3943023/112731
      return (r * 0.299 + g * 0.587 + b * 0.114) > 186 ? '#000000' : '#FFFFFF';
    };
    return color(bgColor) === textColor;
  }

  private colorExists(color: string): boolean {
    return this.appointmentTypes.some(appointmentType => appointmentType.backgroundColor === color);
  }


  async deleteAppointmentType(id: string | number) {
    const confirmed = await this.showConfirmationDialog();

    if (confirmed) {
      this.appointmentTypes = this.appointmentTypes.filter((cb: any) => cb.id !== id);

      if (typeof id === 'string') {
        this.saveAppointmentTypes();
      }
    }
  }

  saveAppointmentTypes() {
    this.appointmentTypeService.updateAppointmentTypes(this.appointmentTypes)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          this.showToastMessage('Appointment types updated successfully');
          this.groupService.setSelectedGroup(this.groupService.group);
        },
        (error) => {
          console.error('Error updating appointment types:', error);
          this.showToastMessage('There was an error updating the appointment types. Please try again.');
        });
  }


  /* saveScheduleSettings() {

    this.scheduleService.updateSchedule({
      id: this.scheduleId,
      groupId: this.groupId,
      availableDays: this.days.filter(day => day.selected).map(day => day.name),
      timeSlotDuration: this.timeSlotDuration,
    })
      .pipe(
        switchMap(response => {
          if (response) {
            return this.appointmentTypeService.updateAppointmentTypes(this.appointmentTypes);
          }
          return EMPTY;
        })
      ).subscribe(
        (response: any) => {
          if (response) {
            this.showToastMessage('Schedule settings updated successfully');
            this.groupService.setSelectedGroup(this.groupService.group);
          }
        },
        (error) => {
          console.error('Error updating schedule settings:', error);
          this.showToastMessage('There was an error updating your schedule settings. Please try again.');
        });
  } */

  /* openAppointmentReminderDialog() {

    let dialogRef = this.dialog.open(this.appointmentReminderDialog!, { width: '600px' });

    dialogRef.afterClosed().subscribe((appointmentReminder: string) => {
      this.groupService.updateGroupSettings({
        id: this.group.id,
        appointmentReminder
      })
        .pipe(takeUntil(this.destroy$)).subscribe(
          (response: any) => {
            if (response) {
              this.showToastMessage('Appointment reminder message updated successfully');
              this.groupService.setSelectedGroup(this.groupService.group);
            }
          },
          (error) => {
            console.error('Error updating appointment reminder message:', error);
            this.showToastMessage('There was an error updating your appointment reminder message. Please try again.');
          });

    });
  } */

  // Survey template linking methods
  private loadSurveyTemplates(): void {
    if (!this.groupId) return;
    
    this.surveyTemplateService.getAllSurveyTemplates(this.groupId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.surveyTemplates = response.data || [];
        },
        error: (error) => {
          console.error('Error loading survey templates:', error);
        }
      });
  }

  private loadLinkedSurveyTemplates(): void {
    if (!this.editingAppointmentType?.id) return;
    
    this.appointmentTypeSurveyTemplateService.getSurveyTemplatesForAppointmentType(this.editingAppointmentType.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.linkedSurveyTemplates = response.data || [];
        },
        error: (error) => {
          console.error('Error loading linked survey templates:', error);
          this.linkedSurveyTemplates = [];
        }
      });
  }

  getLinkedSurveyTemplates(): any[] {
    return this.linkedSurveyTemplates;
  }

  getAvailableSurveyTemplates(): any[] {
    const linkedTemplateIds = this.linkedSurveyTemplates.map(lt => lt.id);
    return this.surveyTemplates.filter(st => !linkedTemplateIds.includes(st.id));
  }

  onSurveyTemplateSelectionChange(surveyTemplateId: string): void {
    if (!surveyTemplateId) return;
    
    this.selectedSurveyTemplateId = surveyTemplateId;
    this.linkSurveyTemplate();
  }

  linkSurveyTemplate(): void {
    if (!this.selectedSurveyTemplateId || !this.editingAppointmentType?.id) return;

    this.isLinking = true;
    this.appointmentTypeSurveyTemplateService.linkSurveyTemplateToAppointmentType(
      this.editingAppointmentType.id,
      this.selectedSurveyTemplateId,
      false
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.showToastMessage('Survey template linked successfully');
          this.loadLinkedSurveyTemplates();
          this.selectedSurveyTemplateId = null;
          this.isLinking = false;
        },
        error: (error) => {
          console.error('Error linking survey template:', error);
          this.showToastMessage('Error linking survey template');
          this.isLinking = false;
        }
      });
  }

  unlinkSurveyTemplate(surveyTemplateId: string): void {
    if (!this.editingAppointmentType?.id) return;

    this.appointmentTypeSurveyTemplateService.unlinkSurveyTemplateFromAppointmentType(
      this.editingAppointmentType.id,
      surveyTemplateId
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.showToastMessage('Survey template unlinked successfully');
          this.loadLinkedSurveyTemplates();
        },
        error: (error) => {
          console.error('Error unlinking survey template:', error);
          this.showToastMessage('Error unlinking survey template');
        }
      });
  }

}